import React, { useState } from 'react';
import { PalletDimensions } from '../types';

interface PalletDimensionsInputProps {
  dimensions: PalletDimensions;
  onDimensionsChange: (dimensions: PalletDimensions) => void;
}

const PRESET_DIMENSIONS = {
  'Standard EUR': { length: 120, width: 80, maxHeight: 170 },
  'Standard US': { length: 100, width: 120, maxHeight: 170 },
  'Custom': null
};

export const PalletDimensionsInput: React.FC<PalletDimensionsInputProps> = ({
  dimensions,
  onDimensionsChange
}) => {
  const [selectedPreset, setSelectedPreset] = useState<string>('Standard EUR');
  const [showCustom, setShowCustom] = useState(false);

  const handlePresetChange = (preset: string) => {
    setSelectedPreset(preset);
    if (preset === 'Custom') {
      setShowCustom(true);
    } else {
      setShowCustom(false);
      const presetDimensions = PRESET_DIMENSIONS[preset as keyof typeof PRESET_DIMENSIONS];
      if (presetDimensions) {
        onDimensionsChange(presetDimensions);
      }
    }
  };

  const handleDimensionChange = (field: keyof PalletDimensions, value: string) => {
    const numValue = parseFloat(value) || 0;
    onDimensionsChange({
      ...dimensions,
      [field]: numValue
    });
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-4 sm:p-6 mb-4 sm:mb-6">
      <h3 className="text-base sm:text-lg font-semibold text-gray-800 mb-3 sm:mb-4">
        Pallet Dimensions
      </h3>
      
      {/* Preset Selection */}
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Pallet Type
        </label>
        <select
          value={selectedPreset}
          onChange={(e) => handlePresetChange(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        >
          {Object.keys(PRESET_DIMENSIONS).map((preset) => (
            <option key={preset} value={preset}>
              {preset}
            </option>
          ))}
        </select>
      </div>

      {/* Custom Dimensions Input */}
      {showCustom && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Length (cm)
            </label>
            <input
              type="number"
              min="1"
              step="0.1"
              value={dimensions.length}
              onChange={(e) => handleDimensionChange('length', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="100"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Width (cm)
            </label>
            <input
              type="number"
              min="1"
              step="0.1"
              value={dimensions.width}
              onChange={(e) => handleDimensionChange('width', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="120"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Max Height (cm)
            </label>
            <input
              type="number"
              min="1"
              step="0.1"
              value={dimensions.maxHeight}
              onChange={(e) => handleDimensionChange('maxHeight', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="170"
            />
          </div>
        </div>
      )}

      {/* Current Dimensions Display */}
      {!showCustom && (
        <div className="bg-gray-50 rounded-md p-3">
          <p className="text-sm text-gray-600">
            <span className="font-medium">Current dimensions:</span> {dimensions.length} × {dimensions.width} × {dimensions.maxHeight} cm
          </p>
        </div>
      )}
    </div>
  );
}; 